import React from 'react';
import { MessagePropertiesContext } from '../../contexts/messageProperties';
import FinanceTerms from '../financeCalculator/financeTerms';
import FinanceFixedDeposit from '../financeCalculator/financeFixedDeposit';
import FinanceDepositRange from '../financeCalculator/financeDepositRange';
import FinanceSummary from '../financeCalculator/financeSummary';
import { V12FinanceData, V12FinanceTerm } from '../../types';

function V12FinanceCalculator({
  financeProperties,
  maxDeposit,
  termsInRage,
  selectedTerm,
  fixedDepositAmount,
  isFixedDepositSelected,
  setIsFixedDepositSelected,
  mappedDepositValue,
  setDepositValue,
  setSelectedTerm,
  disabled = false,
  className = ''
}: Omit<V12FinanceData, 'priceValue' | 'presentationalMonthlyRepayment' | 'financeType' | 'depositValue'> & {
  disabled?: boolean;
  className?: string;
}): React.ReactElement | null {
  const [formattedMessage] = React.useContext(MessagePropertiesContext);

  const setSelectedTermGuard = (term: V12FinanceTerm): void => {
    /* istanbul ignore else */
    if ('deferredPeriod' in term) {
      setSelectedTerm(term);
    }
  };

  if (maxDeposit === null || selectedTerm === null || financeProperties === null || mappedDepositValue === null) {
    return null;
  }

  return (
    <div className={`finance-calculator ${className}`}>
      <FinanceFixedDeposit
        fixedDepositAmount={fixedDepositAmount}
        isSelected={isFixedDepositSelected}
        setIsSelected={setIsFixedDepositSelected}
        disabled={disabled}
      />
      <h4 className="finance-calculator__chooseDeposit">
        {formattedMessage({
          id: 'checkout.components.finance.calculator.chooseDeposit',
          fallback: 'Choose your deposit'
        })}
      </h4>
      <FinanceDepositRange
        min={selectedTerm.minDepositAmount}
        max={maxDeposit}
        value={isFixedDepositSelected && fixedDepositAmount !== null ? fixedDepositAmount : mappedDepositValue}
        setValue={setDepositValue}
        minLabel={`${formattedMessage({
          id: 'checkout.components.finance.calculator.depositRange.min',
          fallback: 'Min'
        })} ${selectedTerm.minDeposit}%`}
        maxLabel={formattedMessage({
          id: 'checkout.components.finance.calculator.depositRange.max',
          fallback: 'Max'
        })}
        disabled={(isFixedDepositSelected && fixedDepositAmount !== null) || disabled}
      />
      <h4 className="finance-calculator__chooseRepayment">
        {formattedMessage({
          id: 'checkout.components.finance.calculator.repaymentTime',
          fallback: 'Choose your repayment length (months)'
        })}
      </h4>
      {termsInRage.length !== 0 && (
        <FinanceTerms
          terms={termsInRage}
          selectedTerm={selectedTerm}
          setSelectedTerm={setSelectedTermGuard}
          disabled={disabled}
        />
      )}
      <hr className="finance-calculator__separator" />
      <FinanceSummary
        depositValue={isFixedDepositSelected && fixedDepositAmount !== null ? fixedDepositAmount : mappedDepositValue}
        term={selectedTerm}
        monthlyRepayment={financeProperties.monthlyRepayment}
        creditAmount={financeProperties.creditAmount}
        interestPayable={financeProperties.interestPayable}
        totalPayable={financeProperties.totalPayable}
      />
    </div>
  );
}

export default V12FinanceCalculator;
